/**
 * CommissionWideSheetExportPage — Issue #372.
 *
 * Renders the template-style wide sheet that mirrors the original
 * 订单提成核算/分配表（多环节）.xlsx layout. The frontend pulls the structured
 * representation from the backend and renders it into a single table; no
 * client-side amount arithmetic is performed.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useCommissionWideSheet,
  type RenderCommissionWideSheet,
} from '@/hooks/useCommission372';

export default function CommissionWideSheetExportPage() {
  const { permissions } = useAuth();
  const canExport = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_EXPORT_RENDER,
  );
  const { sheet, loading, error, render } = useCommissionWideSheet();

  const [filter, setFilter] = useState<RenderCommissionWideSheet>({});

  useEffect(() => {
    // Auto-render once with empty filter on permission grant; the user must
    // hit the 刷新 button to apply filter changes so typing doesn't trigger
    // a backend call on every keystroke.
    if (canExport) void render({});
  }, [canExport, render]);

  if (!canExport) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有渲染提成宽表的权限。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">提成宽表（模板）</h1>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            className="rounded border border-gray-300 px-2 py-1"
            placeholder="订单项目 ID"
            value={filter.orderProjectId ?? ''}
            onChange={(e) =>
              setFilter({ ...filter, orderProjectId: e.target.value || undefined })
            }
          />
          <input
            className="rounded border border-gray-300 px-2 py-1"
            placeholder="员工 ID"
            value={filter.employeeId ?? ''}
            onChange={(e) =>
              setFilter({ ...filter, employeeId: e.target.value || undefined })
            }
          />
          <button
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
            onClick={() => {
              void render(filter);
            }}
          >
            刷新
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : sheet ? (
        <div className="space-y-3">
          <div className="text-lg font-semibold text-center">{sheet.title}</div>
          {sheet.amountsMasked && (
            <div className="text-xs text-amber-700">
              本次渲染金额列已脱敏；如需明文，请联系财务管理员申请权限。
            </div>
          )}
          <div className="overflow-x-auto rounded border bg-white">
            <table className="min-w-[1800px] border-collapse text-xs">
              <thead className="bg-gray-100">
                <tr>
                  {sheet.headers.map((h) => (
                    <th
                      key={h}
                      className="border border-gray-300 px-2 py-1 text-center whitespace-pre-line"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheet.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={sheet.headers.length}
                      className="px-3 py-6 text-center text-muted-foreground border border-gray-300"
                    >
                      暂无数据
                    </td>
                  </tr>
                ) : (
                  sheet.rows.map((row) => (
                    <tr key={`${row.seq}-${row.orderNo ?? ''}`}>
                      <td className="border border-gray-300 px-2 py-1 text-center">
                        {row.seq}
                      </td>
                      <td className="border border-gray-300 px-2 py-1">
                        {row.orderNo ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1">
                        {row.customerName ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 font-mono">
                        {row.customerCode ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1">
                        {row.projectName ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1">
                        {row.deliveredAt ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-right">
                        {row.projectContractDecimal}
                      </td>
                      <td className="border border-gray-300 px-2 py-1">
                        {row.allocationStrategy}
                      </td>
                      {[
                        row.development,
                        row.followUp,
                        row.leadClose,
                        row.assistantClose,
                        row.baseService,
                        row.solutionWriting,
                      ].flatMap((c, i) => [
                        <td
                          key={`emp-${i}`}
                          className="border border-gray-300 px-2 py-1 font-mono"
                        >
                          {c.employeeId
                            ? `${c.employeeId.slice(0, 8)}…`
                            : '-'}
                        </td>,
                        <td
                          key={`rate-${i}`}
                          className="border border-gray-300 px-2 py-1 text-right"
                        >
                          {c.rateDecimal ?? '-'}
                        </td>,
                        <td
                          key={`amt-${i}`}
                          className="border border-gray-300 px-2 py-1 text-right"
                        >
                          {c.commissionAmountDecimal ?? '-'}
                        </td>,
                      ])}
                      <td className="border border-gray-300 px-2 py-1">
                        {row.notes ?? ''}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td
                    colSpan={6}
                    className="border border-gray-300 px-2 py-1 text-right"
                  >
                    合计
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right">
                    {sheet.totalContractDecimal}
                  </td>
                  <td
                    colSpan={sheet.headers.length - 8}
                    className="border border-gray-300 px-2 py-1 text-right"
                  >
                    {sheet.totalCommissionDecimal}
                  </td>
                  <td className="border border-gray-300 px-2 py-1"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-muted-foreground">
          请选择过滤条件并点击刷新
        </div>
      )}
    </div>
  );
}
